/**
 * iter-261 — Publish Stem Pack modal.
 *
 * Opened from the StemExtractorCard after a successful stem extraction.
 * Pre-fills title from the engine's channel name and pulls tempo + key
 * + stem URLs from the iter-253 manifest snapshot owned by
 * `useStemExtraction.lastExtraction`.
 *
 * Auth-gated: if the user isn't signed in we render a sign-in CTA in
 * place of the form.
 */
import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { Upload, X, LogIn, Lock } from "lucide-react";
import { publishStemPack, ALLOWED_GENRES } from "@/lib/marketplaceApi";

const MAX_TITLE = 120;
const MAX_DESCRIPTION = 400;
const MAX_TAGS = 10;

export default function PublishStemPackModal({
  open,
  onClose,
  lastExtraction,
  signedIn,
  onSignIn,
  onPublished,
}) {
  const [title, setTitle] = useState(lastExtraction?.songName || "");
  const [description, setDescription] = useState("");
  const [genre, setGenre] = useState("other");
  const [tagsInput, setTagsInput] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset state when the modal opens with new data.
  React.useEffect(() => {
    if (open) {
      setTitle(lastExtraction?.songName || "");
      setDescription("");
      setGenre("other");
      setTagsInput("");
    }
  }, [open, lastExtraction]);

  const tags = useMemo(() => {
    return tagsInput
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean)
      .slice(0, MAX_TAGS);
  }, [tagsInput]);

  const manifest = lastExtraction?.manifest || {};
  const stems = lastExtraction?.stems || [];

  const handlePublish = async () => {
    if (!title.trim() || title.trim().length < 2) {
      toast.error("Title required", { description: "Enter a title (2-120 characters)." });
      return;
    }
    if (stems.length === 0) {
      toast.error("No stems to publish");
      return;
    }
    setBusy(true);
    try {
      const pack = await publishStemPack({
        title: title.trim(),
        description: description.trim(),
        genre,
        tags,
        stems,
        manifest,
      });
      toast.success("Pack published to marketplace", {
        description: `${pack.title} is now live · share /marketplace`,
      });
      onPublished?.(pack);
      onClose?.();
    } catch (e) {
      if (e.status === 401) {
        toast.error("Sign in required", {
          description: "Use the Sign In button at the top to publish.",
        });
      } else if (e.status === 409) {
        toast.error("Pack cap reached", { description: e.message });
      } else {
        toast.error("Could not publish pack", { description: e.message });
      }
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/70 px-4 py-10 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="publish-stem-pack-modal"
    >
      <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950/80 p-7 shadow-2xl">
        <header className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-white">Publish to Stems Marketplace</h2>
            <p className="mt-1 text-sm text-slate-400">
              Share your {stems.length} extracted stems with the community.
              Other producers can pull them auto-snapped to your tempo + key.
            </p>
          </div>
          <button
            type="button"
            data-testid="publish-modal-close"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-500 transition hover:bg-white/5 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {!signedIn ? (
          <div className="space-y-4 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-5 text-center">
            <Lock className="mx-auto h-7 w-7 text-amber-300" />
            <p className="text-sm text-amber-100">
              Sign in with Google to publish your stems. Browsing the
              marketplace stays open to everyone — only publishing requires
              an account so credit goes to a real producer.
            </p>
            <button
              type="button"
              data-testid="publish-modal-signin-btn"
              onClick={() => { onSignIn?.(); onClose?.(); }}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:brightness-110"
            >
              <LogIn className="h-4 w-4" />
              Sign in with Google
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">
                Pack title
              </label>
              <input
                data-testid="publish-title-input"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE))}
                placeholder="e.g. Mid-tempo Synthwave Stems"
                className="w-full rounded-xl border border-white/10 bg-slate-900/60 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-400/60 focus:outline-none focus:ring-1 focus:ring-violet-400/40"
              />
              <p className="mt-1 text-[10px] text-slate-500">{title.length}/{MAX_TITLE}</p>
            </div>

            {/* Description */}
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">
                Description <span className="normal-case text-slate-600">(optional)</span>
              </label>
              <textarea
                data-testid="publish-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
                rows={3}
                placeholder="What's the vibe? Any notes for remixers?"
                className="w-full resize-none rounded-xl border border-white/10 bg-slate-900/60 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-400/60 focus:outline-none focus:ring-1 focus:ring-violet-400/40"
              />
              <p className="mt-1 text-[10px] text-slate-500">{description.length}/{MAX_DESCRIPTION}</p>
            </div>

            {/* Genre + Tags grid */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">
                  Genre
                </label>
                <select
                  data-testid="publish-genre-select"
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-slate-900/60 px-3 py-2 text-sm text-white focus:border-violet-400/60 focus:outline-none focus:ring-1 focus:ring-violet-400/40"
                >
                  {ALLOWED_GENRES.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">
                  Tags <span className="normal-case text-slate-600">(comma-separated)</span>
                </label>
                <input
                  data-testid="publish-tags-input"
                  type="text"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="lo-fi, chill, sample"
                  className="w-full rounded-xl border border-white/10 bg-slate-900/60 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-400/60 focus:outline-none focus:ring-1 focus:ring-violet-400/40"
                />
                {tags.length > 0 && (
                  <p className="mt-1 truncate text-[10px] text-slate-500">{tags.join(" · ")}</p>
                )}
              </div>
            </div>

            {/* Auto-detected manifest preview */}
            <div className="rounded-xl border border-white/5 bg-slate-900/40 p-3 text-[11px] text-slate-400">
              <p className="font-medium uppercase tracking-wider text-slate-500">Auto-detected metadata</p>
              <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5">
                <span><b className="text-slate-300">Stems:</b> {stems.length}</span>
                <span><b className="text-slate-300">Model:</b> {manifest.stems_model || "htdemucs_6s"}</span>
                {manifest.tempo_bpm && (
                  <span><b className="text-slate-300">Tempo:</b> {Math.round(manifest.tempo_bpm)} BPM</span>
                )}
                {manifest.key && (
                  <span><b className="text-slate-300">Key:</b> {manifest.key}</span>
                )}
              </div>
            </div>

            {/* Publish button */}
            <button
              type="button"
              data-testid="publish-submit-btn"
              onClick={handlePublish}
              disabled={busy || !title.trim()}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Upload className="h-4 w-4" />
              {busy ? "Publishing…" : "Publish to marketplace"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
